import { FormikErrors, useField, useFormikContext } from 'formik';
import React from 'react';
import { FormikTextField } from '../../Components/FormikFields/FormikTextField';
import { StepSelector } from '../../Components/StepSelector/StepSelector';
import { StepSelectorGroup } from '../../Components/StepSelector/StepSelectorGroup';
import { Term } from '../../Config/MedicationConfig';
import styles from './MedicationListInformationStep.module.scss';

export type MedicationInformationStep = {
    name: string;
    isScopeOfPracticeList: boolean;
    isFormulary: boolean;
    isDefaultList: boolean;
};

export const MedicationListFormErrors_TypeMissing = 'formMissingType';

export const createMedicationListStepFields = [
    'name',
    'isScopeOfPracticeList',
    'isFormulary',
    'isDefaultList',
    MedicationListFormErrors_TypeMissing
];

export const MedicationListInformationStep = (props: { verbalOrderTerm: Term }) => {
    const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext<MedicationInformationStep>();

    const scopeOfPracticeField = useField<MedicationInformationStep>('isScopeOfPracticeList');
    const formularyField = useField<MedicationInformationStep>('isFormulary');
    const defaultListField = useField<MedicationInformationStep>('isDefaultList');

    const scopeOfPracticeError = scopeOfPracticeField[1].touched ? scopeOfPracticeField[1].error : '';
    const formularyError = formularyField[1].touched ? formularyField[1].error : '';
    const defaultListError = defaultListField[1].touched ? defaultListField[1].error : '';

    const form = useFormikContext();

    const allTypesTouched = scopeOfPracticeField[1].touched || formularyField[1].touched || defaultListField[1].touched;

    // Typescript shenanigans to make it able to pluck the error off the errors object.
    const typesErrors = allTypesTouched
        ? (
              form.errors as FormikErrors<
                  MedicationInformationStep & {
                      [MedicationListFormErrors_TypeMissing]: string;
                  }
              >
          )[MedicationListFormErrors_TypeMissing]
        : '';

    return (
        <>
            <div className={styles.listName}>
                <FormikTextField label='List name' name='name' testId='list-name' required />
            </div>
            <div className={styles.listHeader}>Type of medication list</div>
            <StepSelectorGroup>
                <StepSelector
                    useCheckbox
                    value={values.isScopeOfPracticeList}
                    active={values.isScopeOfPracticeList}
                    onClick={newValue => {
                        setFieldValue('isScopeOfPracticeList', newValue);
                        setFieldError('isScopeOfPracticeList', '');
                        setFieldTouched('isScopeOfPracticeList', true, false);
                    }}
                    testId='scope-of-practice'
                    text='Scope of practice'
                    info={`A list that includes medications for which a non-prescribing provider can create medication orders. Medications outside of this list require a ${props.verbalOrderTerm?.singularTerm}.`}
                    error={scopeOfPracticeError}
                />
                <StepSelector
                    useCheckbox
                    value={values.isFormulary}
                    active={values.isFormulary}
                    onClick={newValue => {
                        setFieldValue('isFormulary', newValue);
                        setFieldError('isFormulary', '');
                        setFieldTouched('isFormulary', true, false);
                    }}
                    testId='formulary'
                    text='Formulary'
                    info='A limited list of medications available to particular provider specialties.'
                    error={formularyError}
                />
                <StepSelector
                    useCheckbox
                    value={values.isDefaultList}
                    active={values.isDefaultList}
                    onClick={newValue => {
                        setFieldValue('isDefaultList', newValue);
                        setFieldError('isDefaultList', '');
                        setFieldTouched('isDefaultList', true, false);
                    }}
                    testId='default'
                    text='Default list'
                    info='A list of medications which share common prescribing attributes.'
                    error={defaultListError}
                />
                {!!typesErrors && <div className={styles.errorText}>{typesErrors}</div>}
            </StepSelectorGroup>
        </>
    );
};
